import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { SEARCH_CONSOLE_DAYS } from './palo-search-console';
import {
    SearchCalibrationCase,
    SearchJudgementFigures,
    SearchJudgementOverview,
    SearchJudgementVerdict,
} from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/**
 * Below this many judged searches the agreement rate is noise, so the figure is
 * shown as a target rather than a result. The concept puts the bar at 100.
 */
const CALIBRATION_TARGET = 100;

const VERDICTS: SearchJudgementVerdict[] = ['exact', 'usable', 'wrong'];

const percent = (value: number | null | undefined): string =>
    value === null || value === undefined ? '–' : `${(value * 100).toFixed(1).replace('.', ',')} %`;

/**
 * Ansicht 4 — Bewertung. What the judged searches say about the quality of the
 * results, and next to it how far the judge behind that figure may be believed. The
 * two never appear apart: a verdict from a model is worth quoting only where a
 * person has agreed with it on the same hits.
 */
export const SearchJudgementView = () => {
    const dispatch = useThunkDispatch();
    const [overview, setOverview] = useState<SearchJudgementOverview | null>(null);
    const [judgingCase, setJudgingCase] = useState<SearchCalibrationCase | null>(null);
    const [verdicts, setVerdicts] = useState<Record<number, SearchJudgementVerdict>>({});
    const [requesting, setRequesting] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setRequesting(true);

        void dispatch(async (_dispatch, _getState, { api }) => {
            const [overviewResult, caseResult] = await Promise.all([
                api.searchQuality.getJudgements(SEARCH_CONSOLE_DAYS),
                api.searchQuality.getCalibrationCase(),
            ]);
            setOverview(overviewResult.success ? overviewResult.result : null);
            setJudgingCase(caseResult.success ? caseResult.result : null);
            setVerdicts({});
            setRequesting(false);
        });
    }, [dispatch]);

    useEffect(load, [load]);

    const save = () => {
        if (!judgingCase?.term || judgingCase.searchEventId === undefined) {
            return;
        }
        setSaving(true);
        const term = judgingCase.term;
        const searchEventId = judgingCase.searchEventId;
        const graded = judgingCase.hits
            .filter(hit => verdicts[hit.productId] !== undefined)
            .map(hit => ({ productId: hit.productId, rank: hit.rank, verdict: verdicts[hit.productId] }));

        void dispatch(async (_dispatch, _getState, { api }) => {
            await api.searchQuality.saveCalibration(term, searchEventId, graded);
            setSaving(false);
            load();
        });
    };

    const columns: PaloColumn<SearchJudgementFigures>[] = [
        {
            key: 'queryType',
            type: 'classification',
            head: <FormattedMessage id="app.searchConsole.column.queryType" />,
            cell: row => <FormattedMessage id={`app.searchConsole.queryType.${row.queryType ?? 'all'}`} />,
        },
        {
            key: 'searches',
            head: <FormattedMessage id="app.searchConsole.column.searches" />,
            align: 'right',
            variant: 'amount',
            cell: row => row.searches.toLocaleString('de-AT'),
        },
        {
            key: 'firstHitExactShare',
            head: <FormattedMessage id="app.searchConsole.column.firstHitExact" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.firstHitExactShare),
        },
        {
            key: 'exactShare',
            head: <FormattedMessage id="app.searchConsole.verdict.exact" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.exactShare),
        },
        {
            key: 'usableShare',
            head: <FormattedMessage id="app.searchConsole.verdict.usable" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.usableShare),
        },
        {
            key: 'wrongShare',
            head: <FormattedMessage id="app.searchConsole.verdict.wrong" />,
            align: 'right',
            variant: 'amount',
            cell: row => percent(row.wrongShare),
        },
    ];

    const calibration = overview?.calibration;
    const calibrated = (calibration?.searches ?? 0) >= CALIBRATION_TARGET;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                <FormattedMessage
                    id="app.searchConsole.judgement.intro"
                    values={{ days: SEARCH_CONSOLE_DAYS, judge: overview?.judge || '–' }}
                />
            </Box>

            <Note tone={calibrated ? 'ok' : 'warn'}>
                {calibrated ? (
                    <FormattedMessage
                        id="app.searchConsole.judgement.calibrated"
                        values={{
                            searches: calibration?.searches ?? 0,
                            agreement: percent(calibration?.agreementRate),
                            tooKind: calibration?.machineTooKind ?? 0,
                            tooHarsh: calibration?.machineTooHarsh ?? 0,
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="app.searchConsole.judgement.notCalibrated"
                        values={{
                            done: calibration?.searches ?? 0,
                            target: CALIBRATION_TARGET,
                            agreement: percent(calibration?.agreementRate),
                        }}
                    />
                )}
            </Note>

            <PaloListView
                embedded={true}
                columns={columns}
                items={overview ? [overview.total, ...overview.byQueryType] : []}
                getRowId={row => row.queryType ?? 'all'}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.searchConsole.judgement.empty" />}
            />

            <Box sx={{ fontSize: '13px', fontWeight: 640, color: tokens.color.ink, marginTop: '4px' }}>
                <FormattedMessage id="app.searchConsole.judgement.calibrationTitle" />
            </Box>

            {judgingCase?.hasCase && judgingCase.term ? (
                <>
                    <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                        <FormattedMessage
                            id="app.searchConsole.judgement.judgeThis"
                            values={{ term: judgingCase.term, remaining: judgingCase.remaining }}
                        />
                    </Box>

                    {judgingCase.hits.map(hit => (
                        <Box
                            key={hit.productId}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '28px 1fr auto',
                                gap: '12px',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: tokens.color.surface,
                                border: `1px solid ${tokens.color.line}`,
                                borderRadius: '8px',
                            }}
                        >
                            <Box
                                sx={{
                                    fontFamily: tokens.font.mono,
                                    fontSize: '12px',
                                    color: tokens.color.ink3,
                                    textAlign: 'right',
                                }}
                            >
                                {hit.rank}
                            </Box>
                            <Box>
                                <Box sx={{ fontSize: '13px', color: tokens.color.ink }}>{hit.name}</Box>
                                <Box
                                    sx={{ fontFamily: tokens.font.mono, fontSize: '11px', color: tokens.color.accent }}
                                >
                                    {hit.number}
                                    {hit.brand ? ` · ${hit.brand}` : ''}
                                </Box>
                            </Box>
                            <ToggleButtonGroup
                                size="small"
                                exclusive={true}
                                value={verdicts[hit.productId] ?? null}
                                onChange={(_event, value: SearchJudgementVerdict | null) =>
                                    setVerdicts(current => {
                                        // Deselecting drops the hit again: an ungraded hit
                                        // must not travel as a verdict of its own.
                                        const next = { ...current };
                                        if (null === value) {
                                            delete next[hit.productId];
                                        } else {
                                            next[hit.productId] = value;
                                        }
                                        return next;
                                    })
                                }
                            >
                                {VERDICTS.map(verdict => (
                                    <ToggleButton key={verdict} value={verdict} sx={{ fontSize: '11px' }}>
                                        <FormattedMessage id={`app.searchConsole.verdict.${verdict}`} />
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    ))}

                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            disabled={saving || Object.keys(verdicts).length === 0}
                            onClick={save}
                        >
                            <FormattedMessage id="app.searchConsole.judgement.save" />
                        </Button>
                        <Button variant="text" disabled={saving} onClick={load}>
                            <FormattedMessage id="app.searchConsole.judgement.skip" />
                        </Button>
                    </Box>
                </>
            ) : (
                <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                    <FormattedMessage id="app.searchConsole.judgement.nothingToJudge" />
                </Box>
            )}
        </Box>
    );
};

/** A framed remark; the tone colours only its edge, never the whole screen. */
const Note = ({ tone, children }: { tone: 'ok' | 'warn'; children: ReactNode }) => (
    <Box
        sx={{
            fontSize: '12px',
            color: tokens.color.ink,
            padding: '10px 12px',
            borderRadius: '8px',
            backgroundColor: 'ok' === tone ? tokens.color.okSoft : tokens.color.warnSoft,
            border: `1px solid ${'ok' === tone ? tokens.color.okLine : tokens.color.warnLine}`,
        }}
    >
        {children}
    </Box>
);
